import { Color, Spacing } from '@persona-id/design-tokens';
import { ChevronDownIcon, EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';

import GraphVertexIcon from 'dashboard/components/GraphVertexIcon/GraphVertexIcon';
import { DatumTypeToken } from 'dashboard/models/graph';
import { NodeRenderConfigLookupFn } from 'dashboard/models/graph-explorer';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';

export interface GraphExplorerPanelHeaderRowProps {
  label: string;
  selected: boolean;
  dimmed: boolean;
  scrollable: boolean;
  handleClick: (datumTypeToken: DatumTypeToken, selected: boolean) => void;
  handleDimClick: (datumTypeToken: DatumTypeToken, dimmed: boolean) => void;
  handleHover: (datumTypeToken: DatumTypeToken, hover: boolean) => void;
  datumTypeToken: DatumTypeToken;
  children?: ReactNode;
  getNodeRenderConfig: NodeRenderConfigLookupFn;
}

const IconButtonContainer = styled('span')`
  min-width: ${Spacing[2]};
  margin-left: auto;
  margin-right: 0;
  svg {
    vertical-align: text-top;
  }
`;

const CaretButtonContainer = styled('span')<{ dimmed: boolean }>`
  margin-right: ${Spacing[2]};
  transition: transform ${TransitionDuration.Medium};
  svg {
    vertical-align: text-top;
    color: ${(props) => (props.dimmed ? Color.CoreSlate700 : Color.CoreSlate900)};
    & *[stroke] {
      stroke: ${(props) => (props.dimmed ? Color.CoreSlate700 : Color.CoreSlate900)};
    }
  }
  &.collapsed {
    transform: rotate(-90deg);
  }
`;

const AlignedGraphVertexIcon = styled(GraphVertexIcon)`
  margin-top: auto;
  margin-bottom: auto;
`;

const LabelContainer = styled('span')`
  text-overflow: ellipsis;
  max-width: 265px;
  overflow: hidden;
  margin-left: 6px;
`;

const CollapsibleHeading = styled('div')<{
  collapsed: boolean;
  dimmed: boolean;
  scrollable: boolean;
}>`
  padding: 4px 12px 4px 4px;
  font-size: ${({ theme }) => {
    return theme.fontSizeSmall;
  }};
  display: flex;
  flex-direction: row;
  :first-child {
    font-weight: ${FontWeight.Regular};
    background-color: transparent;
    color: ${(props) => (props.dimmed ? Color.CoreSlate700 : 'inherit')};
    border-bottom: ${(props) => (props.scrollable ? `1px solid ${Color.CoreSlate100}` : 'unset')};
  }
`;

const HeadingContainer = styled('div')<{
  activeHighlight: boolean;
  selected: boolean;
  collapsed: boolean;
}>`
  border: ${(props) => {
    if (props.activeHighlight) {
      return `1px solid ${Color.CorePurple800}}`;
    } else if (props.collapsed) {
      return `1px solid transparent`;
    } else {
      return `1px solid ${Color.CoreSlate100}`;
    }
  }};
  border-radius: 4px;
  background-color: ${(props) => (props.selected ? Color.CorePurple100 : '')};

  // Don't show border if there are no contents
  :empty {
    display: none;
  }
`;

interface PanelItemContainerProps {
  dimmed: boolean;
  collapsed: boolean;
}

const PanelItemContainer = styled('div')<PanelItemContainerProps>`
  padding-left: 2px;
  padding-bottom: ${(props) => (props.collapsed ? Spacing[0] : Spacing[2])};

  cursor: pointer;
  text-overflow: ellipsis;

  overflow-y: auto;
  overflow-x: hidden;

  p {
    cursor: pointer;
  }
`;

const StyledEyeIcon = styled(EyeIcon)<{ hover: boolean; dimmed: boolean }>`
  visibility: ${(props) => (props.hover ? 'visible' : 'hidden')};
`;

interface BodyProps {
  collapsed: boolean;
}

const Body = styled('section')<BodyProps>`
  ${({ collapsed }) => collapsed && 'display: none;'}
`;

const GraphExplorerPanelHeaderRow = ({
  label,
  selected,
  dimmed,
  scrollable,
  handleClick,
  handleDimClick,
  handleHover,
  datumTypeToken,
  children,
  getNodeRenderConfig,
}: GraphExplorerPanelHeaderRowProps) => {
  const [activeHighlight, setActiveHighlight] = useState(false);
  const [hover, setHover] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    // If a node is selected, we want to make sure it is expanded and visible in the display panel
    if (selected) {
      setCollapsed(false);
    }
  }, [selected]);

  return (
    <PanelItemContainer
      id={label}
      dimmed={dimmed}
      collapsed={collapsed}
      onClick={(e) => {
        handleClick(datumTypeToken, !selected);
        e.stopPropagation();
      }}
    >
      <HeadingContainer selected={selected} activeHighlight={activeHighlight} collapsed={collapsed}>
        <CollapsibleHeading
          id={label}
          collapsed={collapsed}
          dimmed={dimmed}
          scrollable={scrollable}
          onMouseEnter={(e) => {
            e.stopPropagation();
            setHover(true);
            handleHover(datumTypeToken, true);
            setActiveHighlight(true);
          }}
          onMouseLeave={(e) => {
            e.stopPropagation();
            setHover(false);
            handleHover(datumTypeToken, false);
            setActiveHighlight(false);
          }}
        >
          <CaretButtonContainer
            id={label}
            onClick={(e) => {
              setCollapsed(!collapsed);
              e.stopPropagation();
            }}
            className={collapsed ? 'collapsed' : ''}
            dimmed={dimmed}
          >
            <ChevronDownIcon />
          </CaretButtonContainer>
          <AlignedGraphVertexIcon
            version='v2'
            datumTypeToken={datumTypeToken}
            backgroundNone={false}
            dimmed={dimmed}
            nodeRenderConfig={getNodeRenderConfig(datumTypeToken)}
          />
          <LabelContainer>{label}</LabelContainer>
          <IconButtonContainer
            role='button'
            id={label}
            className={dimmed ? 'dimmed' : ''}
            onClick={(e) => {
              handleDimClick(datumTypeToken, !dimmed);
              e.stopPropagation();
            }}
          >
            {dimmed && <EyeOffIcon />}
            {!dimmed && <StyledEyeIcon hover={hover} dimmed={dimmed} />}
          </IconButtonContainer>
        </CollapsibleHeading>
        <Body collapsed={collapsed}>{children}</Body>
      </HeadingContainer>
    </PanelItemContainer>
  );
};

export default GraphExplorerPanelHeaderRow;
